import React from 'react';
import styled from 'styled-components';
import { css } from 'emotion';
import Box from 'shared/Box';
import Image from 'shared/Image';
import { FieldInput } from 'components/encore/form/Field';
import Icon from 'shared/Icon';
import Mask from 'shared/Mask';
import { Range } from '@spotify-internal/encore-web/content-platform';
import { CenteredLoadingIndicator, Modal } from '@spotify-internal/anchor-ds';
import { useT } from 'client/i18n/hooks/useT';
import InfiniteScroll from 'shared/InfiniteScroll';
import ImageResizerAndCropper from 'shared/ImageResizerAndCropper';
import {
  FormInputIcon,
  IconSearch,
  screenSmMax,
  TextLink,
  Type,
} from '@spotify-internal/encore-web';
import NoResultsSection from './components/NoResultsSection';

const noop = () => null;

const getImageUrlToCropFromSourceImage = (sourceImage: SourceImage) => {
  if (sourceImage.maybeUnsplashImage) {
    return sourceImage.maybeUnsplashImage.fullResUrl;
  }
  if (sourceImage.maybeLocalImageBlobUrl) {
    return sourceImage.maybeLocalImageBlobUrl;
  }
  if (sourceImage.maybeHttpImageUrl) {
    return sourceImage.maybeHttpImageUrl;
  }
  return undefined;
};

const ModalBody = styled(Modal.Body)<{ isHidingImageSearch: boolean }>`
  display: grid;
  grid-gap: 20px;
  grid-template-columns: ${props =>
    props.isHidingImageSearch ? '1fr' : '1fr 1fr'};
  justify-content: center;
  margin-top: 16px;

  @media (max-width: ${screenSmMax}) {
    grid-gap: 16px;
    grid-template-columns: 1fr;
  }
`;

const Container = styled.div`
  display: grid;
  grid-gap: 16px;
  justify-content: center;
`;

const ImageSearchScene = (props: ImageSearchSceneProps) => {
  const t = useT();
  const {
    unsplashImages,
    coverArt,
    searchText,
    onSearchTextChange,
    onRequestMoreItems,
    onChangeZoomLevel,
    zoomLevel,
    xCropPosition,
    yCropPosition,
    onCropPositionChange,
    onSelectImage,
    onCropComplete,
    isHidingImageSearch,
    isLoadingImageToCrop,
  } = props;
  return (
    <>
      {isHidingImageSearch ? (
        <Modal.Title>
          {t(
            'CoverArtModalScreen.ImageSearchScene.CropYourPhotoTitle',
            'Crop your photo',
            'Image search scene modal title to crop the photo'
          )}
        </Modal.Title>
      ) : (
        <>
          <Modal.Title>
            {t(
              'CoverArtModalScreen.ImageSearchScene.SearchForAPhotoTitle',
              'Search for a photo',
              'Image search scene modal title to search for a photo'
            )}
          </Modal.Title>
          <Modal.Subtitle>
            {t(
              'CoverArtModalScreen.ImageSearchScene.SearchForAPhotoSubtitle',
              'This search is powered by Unsplash, which offers beautiful images completely free!',
              'Image search scene modal subtitle to search for a photo using Unsplash'
            )}
          </Modal.Subtitle>
        </>
      )}
      <ModalBody isHidingImageSearch={isHidingImageSearch} as="div">
        <Container>
          <div
            className={css`
              margin: auto;
              border-radius: 8px;
              overflow: hidden;
            `}
          >
            {isLoadingImageToCrop ? (
              <div
                className={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 200px;
                  height: 200px;
                  background-color: #f4f4f5;
                `}
              >
                <CenteredLoadingIndicator />
              </div>
            ) : (
              <ImageResizerAndCropper
                imageUrl={getImageUrlToCropFromSourceImage(
                  coverArt.sourceImage
                )}
                xPosition={xCropPosition}
                yPosition={yCropPosition}
                onPositionChange={onCropPositionChange}
                width={200}
                height={200}
                scale={zoomLevel}
                onCropComplete={onCropComplete}
                shape="rounded"
              />
            )}
          </div>
          {!isLoadingImageToCrop &&
            coverArt.sourceImage.maybeUnsplashImage?.attributionName && (
              <Type variant="viola">
                {t(
                  'CoverArtModalScreen.ImageSearchScene.UnsplashPhotoCredit',
                  'Photo by {artistName} on Unsplash',
                  'Photo credit for the artist if the image came from unsplash',
                  {
                    artistName: (
                      <TextLink
                        key="cover-art-modal__artist-name"
                        href={
                          coverArt.sourceImage.maybeUnsplashImage
                            ?.attributionUrl || '/'
                        }
                      >
                        <Type variant="viola">
                          {coverArt.sourceImage.maybeUnsplashImage &&
                            coverArt.sourceImage.maybeUnsplashImage
                              .attributionName}
                        </Type>
                      </TextLink>
                    ),
                  }
                )}
              </Type>
            )}
          <div
            className={css`
              max-width: 200px;
              margin: 0 auto;
            `}
          >
            <Range
              min={1}
              max={10}
              step={0.1}
              value={zoomLevel}
              onChange={onChangeZoomLevel}
              disabled={isLoadingImageToCrop}
              aria-label={t(
                'CoverArtModalScreen.ImageSearchScene.ZoomInAriaLabel',
                'zoom in or out of image',
                'Aria label to zoom in'
              )}
            />
          </div>
        </Container>
        <Container>
          {!isHidingImageSearch && (
            <>
              <FormInputIcon
                iconLeading={
                  <IconSearch
                    aria-label={t(
                      'CoverArtModalScreen.ImageSearchScene.SearchAriaLabel',
                      'search',
                      'Aria label for form to search for an image to use'
                    )}
                    id="search-icon"
                  />
                }
              >
                <FieldInput
                  name="searchText"
                  value={searchText}
                  aria-label={t(
                    'CoverArtModalScreen.ImageSearchScene.SearchAriaLabel',
                    'search',
                    'Aria label for form to search for an image to use'
                  )}
                  onChange={e => {
                    if (onSearchTextChange) {
                      onSearchTextChange(e.currentTarget.value);
                    }
                  }}
                  aria-describedby="search-icon"
                />
              </FormInputIcon>

              <div
                className={css`
                  position: relative;
                  max-width: 400px;
                  height: 270px;
                `}
              >
                {unsplashImages?.length === 0 ? (
                  <NoResultsSection />
                ) : (
                  <>
                    <InfiniteScroll
                      items={unsplashImages}
                      onRequestMore={onRequestMoreItems}
                      renderInfiniteScrollContent={(
                        images: UnSplashImage[]
                      ) => (
                        <Box display="flex" justifyContent="center" wrap>
                          {/* TODO https://anchorfm.atlassian.net/browse/WHEEL-804: Replace Existing Infinite Scroll Box Component with Loop Component */}
                          {images.map((unsplashImage, i) => (
                            <button
                              className={css`
                                padding: 0px;
                              `}
                              key={unsplashImage.thumbnailUrl}
                              onClick={() => onSelectImage?.(unsplashImage)}
                              aria-label={t(
                                'CoverArtModalScreen.ImageSearchScene.UnsplashImageAriaLabel',
                                'Select image {imageIndex}',
                                'Aria label for form icon to search for an image to use',
                                { imageIndex: i + 1 }
                              )}
                            >
                              <Box
                                position="relative"
                                width={63}
                                height={63}
                                margin={5}
                              >
                                <Mask shape="rounded">
                                  <div
                                    className={css`
                                      button:focus &,
                                      button:hover & {
                                        filter: brightness(0.8);
                                      }
                                    `}
                                  >
                                    <Image
                                      renderLoadingPlaceholder={() => (
                                        <Box
                                          color="#F4F4F5"
                                          width="100%"
                                          height="100%"
                                        />
                                      )}
                                      height={63}
                                      width={63}
                                      imageUrl={unsplashImage.thumbnailUrl}
                                      objectFit="cover"
                                    />
                                  </div>
                                </Mask>
                                {coverArt.sourceImage.maybeUnsplashImage &&
                                  coverArt.sourceImage.maybeUnsplashImage
                                    ?.thirdPartyId ===
                                    unsplashImage.thirdPartyId && (
                                    <Box
                                      position="absolute"
                                      top
                                      left
                                      width="100%"
                                      height="100%"
                                      display="flex"
                                      justifyContent="center"
                                      alignItems="center"
                                    >
                                      <Box
                                        height={37}
                                        width={37}
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                      >
                                        <Box width={24}>
                                          <Icon
                                            type="checkmark"
                                            fillColor="white"
                                          />
                                        </Box>
                                      </Box>
                                    </Box>
                                  )}
                              </Box>
                            </button>
                          ))}
                        </Box>
                      )}
                    />
                    <div
                      className={css`
                        position: absolute;
                        bottom: 0;
                        width: 100%;
                        height: 8px;
                        background: linear-gradient(0, #000000, #ffffff);
                        opacity: 0.1;
                      `}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </Container>
      </ModalBody>
    </>
  );
};

type UnSplashImage = {
  thumbnailUrl: string;
  fullResUrl: string;
  id?: string;
  authorName?: string;
  attributionName?: string;
  attributionUrl?: string;
  thirdPartyId?: string;
};

type SourceImage = {
  maybeUnsplashImage: UnSplashImage;
  maybeLocalImageBlobUrl: string;
  maybeHttpImageUrl: string;
};

type CoverArtProp = {
  maybeCroppedImageUrl?: string;
  maybeFinalImageUrl: string;
  sourceImage: SourceImage;
};

type ImageSearchSceneProps = {
  unsplashImages?: UnSplashImage[];
  searchText?: '';
  onSearchTextChange?: (text: string) => void;
  onRequestMoreItems?: () => void;
  onChangeZoomLevel?: (value: number) => void;
  zoomLevel?: number;
  xCropPosition?: number;
  yCropPosition?: number;
  onCropPositionChange?: (coords: { x: number; y: number }) => void;
  onSelectImage?: (image: UnSplashImage) => void;
  coverArt: CoverArtProp;
  onCropComplete?: (croppedArea: any, croppedAreaPixels: any) => void;
  isHidingImageSearch?: boolean;
  isLoadingImageToCrop?: boolean;
};

ImageSearchScene.defaultProps = {
  unsplashImages: [],
  searchText: '',
  zoomLevel: 1,
  xCropPosition: 0,
  yCropPosition: 0,
  onSearchTextChange: noop,
  onRequestMoreItems: noop,
  onChangeZoomLevel: noop,
  onCropPositionChange: noop,
  onSelectImage: noop,
  onCropComplete: noop,
  isHidingImageSearch: false,
  isLoadingImageToCrop: false,
};

export { ImageSearchScene };
